import { useT } from '@/i18n';
import { Link, Preloader } from 'framework7-react';
import { useRef, type CSSProperties } from 'react';
import { useVoiceTranscription, type VoiceError } from '../hooks/useVoiceTranscription';

export function joinTranscript(base: string, spoken: string): string {
  const head = base.trimEnd();
  const tail = spoken.trim();
  if (!tail) return base;
  return head ? `${head} ${tail}` : tail;
}

interface VoiceInputButtonProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  'data-testid'?: string;
}

export function VoiceInputButton({
  value,
  onChange,
  disabled = false,
  'data-testid': testId = 'voice-input',
}: VoiceInputButtonProps) {
  const t = useT().voiceInput;
  const baseRef = useRef('');
  const voice = useVoiceTranscription((spoken) =>
    onChange(joinTranscript(baseRef.current, spoken)),
  );
  const recording = voice.status === 'listening';
  const busy = voice.status === 'starting' || voice.status === 'transcribing';

  const noteFor = (error: VoiceError): string => {
    if (error.problem === 'permission-denied') return t.permissionDenied;
    if (error.problem === 'no-microphone') return t.noMicrophone;
    if (error.problem === 'unsupported') return t.unsupported;
    return error.detail ?? t.failed;
  };

  const note = voice.error
    ? noteFor(voice.error)
    : recording && !voice.live
      ? t.recordingOnly
      : null;

  const toggle = () => {
    if (recording) {
      void voice.stop();
      return;
    }
    if (voice.status !== 'idle') return;
    baseRef.current = value;
    void voice.start();
  };

  const levelStyle: CSSProperties & Record<'--voice-level', string> = {
    '--voice-level': voice.level.toFixed(2),
  };

  return (
    <span className={`voice-input${recording ? ' voice-input-recording' : ''}`} style={levelStyle}>
      {busy ? (
        <span className="voice-input-busy" data-testid={`${testId}-busy`}>
          <Preloader size={20} />
        </span>
      ) : (
        <Link
          href={false}
          className={`voice-input-button${disabled || !voice.available ? ' disabled' : ''}`}
          iconF7={recording ? 'mic_fill' : 'mic'}
          aria-label={recording ? t.stop : t.start}
          aria-pressed={recording}
          data-testid={testId}
          onClick={toggle}
        />
      )}
      {note && (
        <span
          className={`voice-input-note${voice.error ? ' voice-input-note-error' : ''}`}
          role={voice.error ? 'alert' : 'status'}
          data-testid={`${testId}-note`}
        >
          {note}
        </span>
      )}
    </span>
  );
}
